import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from './envelope';

/**
 * The canonical 7-method contract every entity service in this package
 * exposes. Aligns with the live swagger spec at
 * `https://api.knottique.kristysoftware.com/swagger/v1/swagger.json`.
 *
 * - `T` — the entity row shape (e.g. `Category`).
 * - `C` — the create payload (usually `Omit<T, ...timestamps + id>`).
 * - `F` — the filter payload, typically `Partial<Pick<T, ...>>`.
 *
 * The service intentionally hides all wire details — verb / body shape /
 * envelope unwrapping — from callers. Migration only needs to touch the
 * factory below; the ~50 callsites consume the same `Promise<T>` /
 * `Promise<T[]>` / `Promise<void>` returns they always have.
 */
export interface CrudService<T, C, F> {
  /** `POST {basePath}/all` — list every active row. */
  all(): Promise<T[]>;
  /** `POST {basePath}/Get` body `{ [WireKey]: id }` — fetch one by id. */
  get(id: string): Promise<T>;
  /** `POST {basePath}/find` body `filter` — fetch the first row matching. */
  find(filter: F): Promise<T>;
  /** `POST {basePath}/filter` body `filter` — fetch every row matching. */
  filter(filter: F): Promise<T[]>;
  /** `POST {basePath}` body `{ items }` — bulk insert; returns created rows. */
  create(items: C[]): Promise<T[]>;
  /** `PUT {basePath}` body `{ items }` — bulk update; returns updated rows. */
  update(items: T[]): Promise<T[]>;
  /** `POST {basePath}/Delete` body `{ [WireKey]: id }` — soft / hard delete. */
  delete(id: string): Promise<void>;
}

/**
 * Build a {@link CrudService} for a given entity. The factory derives the
 * wire-level id key by capitalising the first character of `idKey` — so
 * `idKey: 'categoryId'` produces body `{ CategoryId: '<uuid>' }`, matching
 * the legacy query-param casing the backend was already accepting.
 *
 * @param client     Shared axios instance (auto-attaches Authorization).
 * @param basePath   Entity URL prefix, e.g. `'/Category'` or `'/AttributeValue'`.
 *                   No trailing slash. The factory appends `/all`, `/Get`, etc.
 * @param idKey      The entity's primary-key field on the data interface
 *                   (camelCase). Used to wrap the id in the request body.
 *
 * @example
 *   export function createCategoryService(c: AxiosInstance) {
 *     return createCrudService<Category, CategoryCreate, CategoryFilter>(
 *       c, '/Category', 'categoryId'
 *     );
 *   }
 */
export function createCrudService<T, C, F>(
  client: AxiosInstance,
  basePath: string,
  idKey: string,
): CrudService<T, C, F> {
  // Backend's [FromBody] binder generally accepts either casing, but the
  // legacy code always sent PascalCase (e.g. `CategoryId`). Match that to
  // minimise wire-level surprises if the binder was configured strict.
  const wireKey = idKey.charAt(0).toUpperCase() + idKey.slice(1);

  return {
    all: () =>
      client
        .post<ObjectResponse<T[] | { items?: T[] | null }>>(`${basePath}/all`, {})
        .then((r) => unwrapItems<T>(r.data)),

    get: (id: string) =>
      client
        .post<ObjectResponse<T>>(`${basePath}/Get`, { [wireKey]: id })
        .then((r) => unwrap(r.data)),

    find: (filter: F) =>
      client
        .post<ObjectResponse<T>>(`${basePath}/find`, filter)
        .then((r) => unwrap(r.data)),

    filter: (filter: F) =>
      client
        .post<ObjectResponse<T[] | { items?: T[] | null }>>(`${basePath}/filter`, filter)
        .then((r) => unwrapItems<T>(r.data)),

    create: (items: C[]) =>
      client
        .post<ObjectResponse<T[] | { items?: T[] | null }>>(basePath, { items })
        .then((r) => unwrapItems<T>(r.data)),

    update: (items: T[]) =>
      client
        .put<ObjectResponse<T[] | { items?: T[] | null }>>(basePath, { items })
        .then((r) => unwrapItems<T>(r.data)),

    delete: (id: string) =>
      client
        .post<ObjectResponse<null>>(`${basePath}/Delete`, { [wireKey]: id })
        .then((r) => {
          unwrap(r.data, { allowNullData: true });
        }),
  };
}
